/**
 * Habita — WhatsApp helper compartido.
 *
 * Deep-link a wa.me con teléfono digits-only (default Colombia +57) y un
 * mensaje pre-armado. Diseñado para funcionar tanto en `PropertyCard`
 * (mensaje simple, sin URL — la ficha aún no fue abierta) como en
 * `PropertyDetail` (mensaje enriquecido con la URL de la ficha).
 */

/** Convierte un teléfono libre a dígitos con prefijo país si es celular colombiano. */
export function normalizeColombiaPhone(phone) {
  if (!phone) return null;
  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;
  // Celular colombiano típico: 10 dígitos empezando en 3 → prepend 57
  if (digits.length === 10 && digits.startsWith('3')) digits = '57' + digits;
  return digits;
}

/**
 * Construye un link https://wa.me/... con mensaje pre-armado.
 *
 * Firma canónica: `buildPropertyWhatsAppLink(phone, property, opts)`.
 * Firma alternativa (Advisor lead): pasar un solo objeto con
 *   `{ phone?, property, advisorContext, variant? }`. Cuando no hay
 *   teléfono usa `wa.me/?text=...` (deja al usuario elegir contacto).
 *
 * @returns {string|null}
 */
export function buildPropertyWhatsAppLink(...args) {
  let phone, property, opts = {};
  if (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0]) && (args[0].property || args[0].advisorContext)) {
    // Firma nueva (Advisor lead)
    ({ phone, property, ...opts } = args[0]);
  } else {
    [phone, property, opts = {}] = args;
  }
  const digits = normalizeColombiaPhone(phone);
  const variant = opts.variant || (opts.advisorContext ? 'advisor' : 'card');
  const title = property?.title || 'la propiedad';
  let msg;
  if (variant === 'advisor' && opts.advisorContext) {
    // Lead inteligente con razonamiento del Advisor
    const score = opts.advisorContext.habita_score;
    const budgetHint = opts.advisorContext.budget_hint;
    const parts = [
      `Hola, me interesa la propiedad "${title}"`,
    ];
    if (typeof score === 'number') {
      parts.push(`porque mi Habita Advisor la calificó con ${score}%`);
    }
    if (budgetHint) {
      parts.push(`para ${budgetHint}`);
    }
    parts.push('. ¿Podrías darme más información y agendar una visita?');
    msg = parts.join(' ').replace(' . ', '. ');
    if (typeof window !== 'undefined' && property?.id) {
      msg += `\n\n${window.location.origin}/propiedad/${property.id}`;
    }
  } else if (variant === 'detail') {
    const url = (typeof window !== 'undefined') ? window.location.href : '';
    msg = `Hola, vi tu publicación en habita.co y estoy interesado en "${title}". ¿Podrías brindarme más información?${url ? `\n\n${url}` : ''}`;
  } else {
    msg = `Hola, me interesa la propiedad "${title}" publicada en Habita.`;
  }
  const base = digits ? `https://wa.me/${digits}` : 'https://wa.me/';
  return `${base}?text=${encodeURIComponent(msg)}`;
}

/** Ícono WhatsApp inline SVG — evita dependencias extra de iconos. */
export const WhatsAppSvgPath = 'M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.04 1.397-1.04 2.36 0 .945.387 1.804.978 2.55 1.247 1.578 2.81 2.95 4.652 3.756.7.301 1.516.673 2.288.673 1.05 0 2.135-.516 2.564-1.49.13-.302.215-.626.215-.958 0-.616-.45-.97-.96-1.13-.318-.103-1.13-.485-1.44-.485zm-3.04 9.295a11.93 11.93 0 0 1-6.18-1.7l-.43-.258-4.51 1.193 1.196-4.388-.276-.43a11.93 11.93 0 0 1-1.83-6.348c0-6.604 5.387-11.992 11.99-11.992 3.207 0 6.21 1.246 8.47 3.51a11.94 11.94 0 0 1 3.515 8.485c-.002 6.604-5.39 11.928-11.945 11.928zm0-25.5C8.05 1 1.5 7.55 1.5 15.57c0 2.555.668 5.05 1.94 7.255L1.5 30.5l7.84-2.06A14.34 14.34 0 0 0 16.07 30.5h.006c8.02 0 14.57-6.55 14.57-14.57.002-3.89-1.512-7.55-4.26-10.302C23.642 2.86 19.98 1 16.073 1z';

export function WhatsAppIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
      <path d={WhatsAppSvgPath} />
    </svg>
  );
}
